import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { justifyContent, space } from 'styled-system';
import type { JustifyContentProps, SpaceProps } from 'styled-system';

import DrawerForm from '../DrawerForm';
import Spinner from '../Spinner';
import DrawerContent from './DrawerContent';
import { useDrawerProps } from './hooks/useDrawerProps';

interface DrawerProps {
  sku: string;
  url: string;
  title: Nullable<string>;
  closeDrawer: () => void;
}

const SPINNER_SIZE = 24;

const StyledSpinner = styled(Spinner)<SpaceProps & JustifyContentProps>`
  ${space};
  ${justifyContent};
`;

const spinnerStyles: ComponentProps<typeof StyledSpinner> = {
  justifyContent: 'center',
  p: 'sp_8',
  strokeColor: 'sys.neutral.border.hover',
};

const containerStyles: ComponentProps<typeof DrawerForm>['bodyStyles'] = {
  px: 'sp_0',
  py: 'sp_0',
  pb: ['sp_16', 'sp_20', 'sp_24', 'sp_32'],
};

const VariationDrawer = ({ sku, url, title, closeDrawer }: DrawerProps) => {
  const { selectedValue, form, images, fabricSample, data, count, isLoading, error } =
    useDrawerProps(sku);

  return (
    <DrawerForm
      ctaLabel={form.ctaLabel}
      isCtaLoading={false}
      isCtaActive={!!selectedValue}
      onSubmit={form.onSubmit}
      error={error}
      bodyStyles={containerStyles}
      secondaryBtnOnClick={closeDrawer}
      secondaryBtnLabel={form.backLabel}
    >
      {!isLoading && data && (
        <DrawerContent
          isVariationDrawer
          sku={sku}
          url={url}
          variationKey={data.key}
          items={data.groups}
          count={count}
          title={title}
          selectedValue={selectedValue}
          images={images}
          fabricSample={fabricSample}
          onChange={form.onChange}
          renderingType={data.renderingType}
        />
      )}
      {isLoading && <StyledSpinner {...spinnerStyles} size={SPINNER_SIZE} />}
    </DrawerForm>
  );
};

export default VariationDrawer;
